import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ButtonGroup from '@mui/material/ButtonGroup';

import FilterService from '../../../../data/filter-service';
import SystemService from '../../../../data/system-service';
import DataContext from '../../../../data/data-context';
import Session from './session';
import SessionService from '../../../../data/session-service';

import RuleHandler from './rulehandler';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
    },
    button: {
        marginRight: theme.spacing(1),
    },
}));

const Handler = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const routeParams = useParams();
    const [filterService, setFilterService] = useState();
    const [sessionService, setSessionService] = useState();
    const { systemSettings } = useContext(DataContext);
    const [sessions, setSessions] = useState([]);
    const [selectedButton, setSelectedButton] = useState('Inbound Rules');
    const [data, setData] = useState({
        TrafficMirrorFilterId: '',
        IngressFilterRules: [],
        EgressFilterRules: [],
        NetworkServices: [],
        Description: '',
        Tags: [
            {
                Key: '',
                Value: '',
            },
        ],
    });

    const handleButtonClick = (buttonName) => {
        setSelectedButton(buttonName);
    };

    const handleIngressRuleChange = (newIngressData) => {
        setData((prevData) => ({
            ...prevData,
            IngressFilterRules: newIngressData,
        }));
    };

    const handleEgressRuleChange = (newEgressData) => {
        setData((prevData) => ({
            ...prevData,
            EgressFilterRules: newEgressData,
        }));
    };

    const calculateTotalRuleSize = () => {
        const ingressRuleSize = data.IngressFilterRules.length;
        const egressRuleSize = data.EgressFilterRules.length;
        return ingressRuleSize + egressRuleSize;
    };

    const handleRuleHandlerClose = () => {
        navigate('/system/port_mirroring/aws', { state: { tab: 1 } });
    };

    useEffect(() => {
        if (systemSettings && systemSettings.aws) {
            SystemService.awsCredAccess().then((res) => {
                if (res) {
                    const region = systemSettings.aws.as_conf.region;
                    const accessKeyId = systemSettings.aws.user_creds.access_key_id;
                    const secretAccessKey = res.AWSSK;
                    initializeService(region, accessKeyId, secretAccessKey);
                }
            });
        }
    }, [systemSettings]);

    const initializeService = async (awsregion, accessKeyId, secretAccessKey) => {
        const config = {
            region: awsregion,
            credentials: {
                accessKeyId,
                secretAccessKey,
            },
        };
        const sessionService = new SessionService(config);
        const filterservice = new FilterService(config);
        setFilterService(filterservice);
        setSessionService(sessionService);
    };

    const getSessions = async (filterId) => {
        let data = {};
        try {
            data = { 'traffic-mirror-filter-id': filterId };
            const sessions = await sessionService.getSessions(data);
            return sessions;
        } catch (error) {
            console.error('Error fetching sessions:', error);
            return null;
        }
    };

    const getFilterId = async (TrafficMirrorFilterId) => {
        try {
            if (!filterService || typeof filterService.getFilterById !== 'function') {
                throw new Error(
                    'Filter service is not properly initialized or getFilterById method is not available'
                );
            }
            const res = await filterService.getFilterById(TrafficMirrorFilterId);

            if (res) {
                return res;
            } else {
                throw new Error('Filter not found');
            }
        } catch (error) {
            console.error('Error fetching filter by Id:', error);
            toast.error(`Failed to fetch filter by Id: ${error.message}`);
            return null;
        }
    };

    useEffect(() => {
        const fetchFilter = async () => {
            if (
                routeParams.TrafficMirrorFilterId &&
                filterService &&
                sessionService
            ) {
                try {
                    const filterId = routeParams.TrafficMirrorFilterId;
                    const filter = [filterId];
                    const filter_res = await getFilterId(filter);
                    const session_res = await getSessions(filterId);
                    if (filter_res && session_res) {
                        setData(filter_res[0]);
                        setSessions(session_res || []);
                    }
                } catch (error) {
                    console.error('Error fetching filter:', error);
                }
            }
        };

        fetchFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterService]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper>
                    <Box p={5}>
                        <Typography variant="h6">
                            Configure Traffic Mirror Filter Rules
                        </Typography>
                        <Grid container spacing={2} alignItems="top" mt={2}>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        label="Filter Description"
                                        name="description"
                                        value={data.Description || ' '}
                                        disabled={true}
                                    />
                                </FormControl>
                                <Box mt={4}>
                                    <Box mt={2}>
                                        <FormLabel>Network Service</FormLabel>
                                    </Box>
                                    <FormControlLabel
                                        label="amazon-dns"
                                        labelPlacement="start"
                                        disabled={true}
                                        control={
                                            <Checkbox
                                                checked={
                                                    data.NetworkServices.indexOf('amazon-dns') !== -1
                                                }
                                                name="checkbox"
                                                color="primary"
                                            />
                                        }
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid mt={3}>
                            <ButtonGroup color="primary" aria-label="primary button group">
                                <Button
                                    variant={
                                        selectedButton === 'Inbound Rules' ? 'contained' : ''
                                    }
                                    onClick={() => handleButtonClick('Inbound Rules')}
                                >
                                    Inbound rules
                                </Button>
                                <Button
                                    variant={
                                        selectedButton === 'Outbound Rules' ? 'contained' : ''
                                    }
                                    onClick={() => handleButtonClick('Outbound Rules')}
                                >
                                    Outbound rules
                                </Button>
                                <Button
                                    variant={selectedButton === 'Sessions' ? 'contained' : ''}
                                    onClick={() => handleButtonClick('Sessions')}
                                >
                                    Sessions
                                </Button>
                            </ButtonGroup>
                            {selectedButton === 'Inbound Rules' && (
                                <RuleHandler
                                    data={data.IngressFilterRules}
                                    filterId={routeParams.TrafficMirrorFilterId}
                                    type={'Inbound'}
                                    onChange={handleIngressRuleChange}
                                    totalRuleSize={calculateTotalRuleSize()}
                                />
                            )}
                            {selectedButton === 'Outbound Rules' && (
                                <RuleHandler
                                    data={data.EgressFilterRules}
                                    filterId={routeParams.TrafficMirrorFilterId}
                                    type={'Outbound'}
                                    onChange={handleEgressRuleChange}
                                    totalRuleSize={calculateTotalRuleSize()}
                                />
                            )}
                            {selectedButton === 'Sessions' && (
                                <Session sessiondata={sessions} />
                            )}
                            <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.button}
                                    onClick={handleRuleHandlerClose}
                                >
                                    Close
                                </Button>
                            </Box>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Handler;
